// src/utils/storage.js
const PREFIX =
  (typeof process !== 'undefined' &&
  process.env &&
  typeof process.env.VUE_APP_PREFIX === 'string'
    ? process.env.VUE_APP_PREFIX.trim()
    : '') || '';

const SEP = ':';

const withPrefix = (key) => (PREFIX ? `${PREFIX}${SEP}${key}` : key);

const hasLocalStorage = (() => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return false;
    const t = '__ls_check__';
    window.localStorage.setItem(t, '1');
    window.localStorage.removeItem(t);
    return true;
  } catch {
    return false;
  }
})();

const memoryFallback = new Map();

export const storage = {
  /** Return the fully prefixed key */
  key(key) {
    return withPrefix(key);
  },

  /** String API (mirrors localStorage) */
  getItem(key) {
    const k = withPrefix(key);
    return hasLocalStorage
      ? window.localStorage.getItem(k)
      : memoryFallback.get(k) ?? null;
  },

  setItem(key, value) {
    const k = withPrefix(key);
    if (hasLocalStorage) {
      window.localStorage.setItem(k, value);
    } else {
      memoryFallback.set(k, value);
    }
  },

  removeItem(key) {
    const k = withPrefix(key);
    if (hasLocalStorage) {
      window.localStorage.removeItem(k);
    } else {
      memoryFallback.delete(k);
    }
  },

  /** JSON helpers (safe parse/stringify) */
  get(key, fallback = null) {
    const raw = this.getItem(key);
    if (raw == null) return fallback;
    try {
      return JSON.parse(raw);
    } catch {
      return fallback;
    }
  },

  set(key, value) {
    this.setItem(key, JSON.stringify(value));
  },

  /** Clear only this app’s namespace (by prefix) */
  clearNamespace() {
    if (!PREFIX) return;
    const pref = `${PREFIX}${SEP}`;
    if (hasLocalStorage) {
      for (let i = window.localStorage.length - 1; i >= 0; i--) {
        const k = window.localStorage.key(i);
        if (k && k.startsWith(pref)) window.localStorage.removeItem(k);
      }
    } else {
      for (const k of memoryFallback.keys()) {
        if (k.startsWith(pref)) memoryFallback.delete(k);
      }
    }
  },
};
